import { useCallback } from 'react'
import type {
  EnrollmentStepSubmissionData,
  useUpdateEnrollmentEndorsements
} from '@/api/enrollment-api'
import { ENROLLMENT_COMMON_MESSAGES } from '../../../../constants/enrollmentContent'
import type { EligibilityFormData } from '../../../../components/eligibilitySteps/types'
import { SELF_ENROLLMENT_STEPS } from '../../utils/steps'
import {
  buildAdditionalInfoStepData,
  buildLocationSelectionStepData,
  buildTrainingDateStepData
} from './payloads'

type HandleSubmitStepFn = (
  step: number,
  data: EnrollmentStepSubmissionData,
  options?: {
    fallbackNextStep?: number
    forcedNextStep?: number
    onSuccess?: () => void
  }
) => Promise<void>

type UseEnrollmentJourneyContinueHandlersArgs = {
  enrollmentSessionToken: string | null
  formData: EligibilityFormData
  handleSubmitStep: HandleSubmitStepFn
  navigate: (to: string, options?: { replace?: boolean; state?: Record<string, unknown> }) => void
  setUploadDocumentsSkipped: (skipped: boolean) => void
  setDefaultStepSaveError: (error: unknown) => void
  setStepSubmissionError: (value: string | null) => void
  updateEnrollmentEndorsementsMutation: ReturnType<typeof useUpdateEnrollmentEndorsements>
}

export function useEnrollmentJourneyContinueHandlers({
  enrollmentSessionToken,
  formData,
  handleSubmitStep,
  navigate,
  setUploadDocumentsSkipped,
  setDefaultStepSaveError,
  setStepSubmissionError,
  updateEnrollmentEndorsementsMutation
}: UseEnrollmentJourneyContinueHandlersArgs) {
  const handleAdditionalInfoContinue = useCallback(
    async (shouldShowLaptopQuestion: boolean) => {
      if (!enrollmentSessionToken) {
        setStepSubmissionError(ENROLLMENT_COMMON_MESSAGES.sessionExpired)
        navigate('/application', { replace: true })
        return
      }

      setStepSubmissionError(null)

      try {
        await updateEnrollmentEndorsementsMutation.mutateAsync({
          sessionToken: enrollmentSessionToken,
          endorsements: formData.endorsements
        })

        await handleSubmitStep(
          SELF_ENROLLMENT_STEPS.ADDITIONAL_INFO,
          buildAdditionalInfoStepData(formData, shouldShowLaptopQuestion),
          {
            fallbackNextStep: SELF_ENROLLMENT_STEPS.TRAINING_DATE
          }
        )
      } catch (error) {
        setStepSubmissionError(
          error instanceof Error ? error.message : ENROLLMENT_COMMON_MESSAGES.saveEndorsementsFailed
        )
      }
    },
    [
      enrollmentSessionToken,
      formData,
      handleSubmitStep,
      navigate,
      setStepSubmissionError,
      updateEnrollmentEndorsementsMutation
    ]
  )

  const handleAdditionalInfoContinueWithoutEndorsements = useCallback(
    async (shouldShowLaptopQuestion: boolean) => {
      setStepSubmissionError(null)

      try {
        if (enrollmentSessionToken) {
          await updateEnrollmentEndorsementsMutation.mutateAsync({
            sessionToken: enrollmentSessionToken,
            endorsements: []
          })
        }

        await handleSubmitStep(
          SELF_ENROLLMENT_STEPS.ADDITIONAL_INFO,
          buildAdditionalInfoStepData(formData, shouldShowLaptopQuestion),
          {
            fallbackNextStep: SELF_ENROLLMENT_STEPS.TRAINING_DATE
          }
        )
      } catch (error) {
        setStepSubmissionError(
          error instanceof Error ? error.message : ENROLLMENT_COMMON_MESSAGES.saveEndorsementsFailed
        )
      }
    },
    [
      enrollmentSessionToken,
      formData,
      handleSubmitStep,
      setStepSubmissionError,
      updateEnrollmentEndorsementsMutation
    ]
  )

  const handleLocationSelectionContinue = useCallback(
    (locationValue = formData.location) => {
      setStepSubmissionError(null)

      if (!locationValue) {
        setStepSubmissionError('Please select a location to continue.')
        return
      }

      handleSubmitStep(
        SELF_ENROLLMENT_STEPS.LOCATION_SELECTION,
        buildLocationSelectionStepData(formData, locationValue),
        {
          fallbackNextStep: SELF_ENROLLMENT_STEPS.ADDITIONAL_INFO
        }
      ).catch(setDefaultStepSaveError)
    },
    [formData, handleSubmitStep, setDefaultStepSaveError, setStepSubmissionError]
  )

  const handleTrainingDateContinue = useCallback(() => {
    handleSubmitStep(
      SELF_ENROLLMENT_STEPS.TRAINING_DATE,
      buildTrainingDateStepData(formData),
      { fallbackNextStep: SELF_ENROLLMENT_STEPS.UPLOAD_DOCUMENTS }
    ).catch(setDefaultStepSaveError)
  }, [formData, handleSubmitStep, setDefaultStepSaveError])

  const handleDocumentsContinue = useCallback((options?: { skipUploadStep?: boolean }) => {
    setUploadDocumentsSkipped(Boolean(options?.skipUploadStep))

    handleSubmitStep(
      options?.skipUploadStep
        ? SELF_ENROLLMENT_STEPS.SIGN_DOCUMENTS
        : SELF_ENROLLMENT_STEPS.UPLOAD_DOCUMENTS,
      {},
      { forcedNextStep: SELF_ENROLLMENT_STEPS.SIGN_DOCUMENTS }
    ).catch(setDefaultStepSaveError)
  }, [handleSubmitStep, setDefaultStepSaveError, setUploadDocumentsSkipped])

  return {
    handleAdditionalInfoContinue,
    handleAdditionalInfoContinueWithoutEndorsements,
    handleDocumentsContinue,
    handleLocationSelectionContinue,
    handleTrainingDateContinue
  }
}
